import { useCallback, useEffect, useMemo, useState } from "react";
import { axiosInstance } from "lib/axios";
import { SearchCredentialService } from "../services/search-credentials.service";
import {
  getSearchCredentialSettings,
  normalizeSearchCredential,
  normalizeSearchCredentialResponse,
  saveSearchCredentialSettings,
  SEARCH_CREDENTIAL_SETTINGS_CHANGED,
  SEARCH_PROVIDER_TYPES,
} from "utils/search-credentials-settings.util";

let credentialsRequest = null;

function loadCredentials(service) {
  if (!credentialsRequest) {
    credentialsRequest = Promise.all(
      [SEARCH_PROVIDER_TYPES.ebsco, SEARCH_PROVIDER_TYPES.proQuest].map((type) =>
        service.getCredentials(type).then((response) => ({ type, response }))
      )
    ).then((responses) => {
      const successfulResponses = responses.filter(
        ({ response }) => response.success
      );
      if (successfulResponses.length === 0) return responses[0].response;

      const credentials = successfulResponses.reduce(
        (settings, response) => {
          const providerSettings = normalizeSearchCredentialResponse(
            response.response.data
          );
          return {
            ...settings,
            [response.type]: providerSettings[response.type],
          };
        },
        getSearchCredentialSettings()
      );
      saveSearchCredentialSettings(credentials);
      return {
        success: true,
        credentials,
      };
    });
  }

  return credentialsRequest;
}

export function useSearchCredentials() {
  const service = useMemo(() => new SearchCredentialService(axiosInstance), []);
  const [credentials, setCredentials] = useState(getSearchCredentialSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [savingType, setSavingType] = useState(null);

  const refresh = useCallback(async () => {
    credentialsRequest = null;
    const response = await loadCredentials(service);
    if (response.success) {
      setCredentials(response.credentials);
    }
    setIsLoading(false);
    return response;
  }, [service]);

  useEffect(() => {
    let cancelled = false;

    const handleSettingsChanged = () => {
      setCredentials(getSearchCredentialSettings());
    };

    window.addEventListener(
      SEARCH_CREDENTIAL_SETTINGS_CHANGED,
      handleSettingsChanged
    );

    loadCredentials(service).then((response) => {
      if (cancelled) return;
      if (response.success) setCredentials(response.credentials);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      window.removeEventListener(
        SEARCH_CREDENTIAL_SETTINGS_CHANGED,
        handleSettingsChanged
      );
    };
  }, [service]);

  const saveCredential = useCallback(
    async (payload) => {
      setSavingType(payload.credentialType);
      const response = await service.saveCredential(payload);
      setSavingType(null);

      if (response.success) {
        const next = {
          ...credentials,
          [payload.credentialType]: normalizeSearchCredential(
            {
              ...credentials[payload.credentialType],
              ...payload,
              hasSavedCredential: true,
            },
            payload.credentialType
          ),
        };
        saveSearchCredentialSettings(next);
        setCredentials(next);
      }

      return response;
    },
    [credentials, service]
  );

  return {
    credentials,
    isLoading,
    savingType,
    saveCredential,
    refresh,
  };
}
